/**
 * React hook for checking user permissions and groups
 * Provides easy access to permission checking functions
 */

import { useMemo } from 'react';
import useAuthStore from '../stores/useAuthStore';
import {
  getUserGroups,
  hasGroup,
  hasAnyGroup,
  hasAllGroups,
  hasPermission,
  getUserPermissions,
} from '../utils/permissions';

/**
 * Hook to access user permissions and groups
 * @returns {Object} Permission checking functions and user info
 */
export const usePermissions = () => {
  const tokens = useAuthStore((state) => state.tokens);
  const idToken = tokens?.idToken;

  const groups = useMemo(() => {
    if (!idToken) return [];
    return getUserGroups(idToken);
  }, [idToken]);

  const permissions = useMemo(() => {
    if (!idToken) return {};
    return getUserPermissions(idToken);
  }, [idToken]);

  const checkGroup = (groupName) => {
    if (!idToken) return false;
    return hasGroup(idToken, groupName);
  };

  const checkAnyGroup = (groupNames) => {
    if (!idToken) return false;
    return hasAnyGroup(idToken, groupNames);
  };

  const checkAllGroups = (groupNames) => {
    if (!idToken) return false;
    return hasAllGroups(idToken, groupNames);
  };

  const checkPermission = (permission) => {
    if (!idToken) return false;
    return hasPermission(idToken, permission);
  };

  const isAdmin = useMemo(() => {
    if (!idToken) return false;
    return hasGroup(idToken, 'admin');
  }, [idToken]);

  const isManager = useMemo(() => {
    if (!idToken) return false;
    return hasGroup(idToken, 'manager');
  }, [idToken]);

  const isAdminOrManager = useMemo(() => {
    if (!idToken) return false;
    return hasAnyGroup(idToken, ['admin', 'manager']);
  }, [idToken]);

  return {
    groups,
    permissions,
    checkGroup,
    checkAnyGroup,
    checkAllGroups,
    checkPermission,
    isAdmin,
    isManager,
    isAdminOrManager,
  };
};

export default usePermissions;

